import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { GraduationCap, CheckCircle, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';

// Lista de códigos de país con banderas (emojis)
const countryCodes = [
  { code: '+52', country: 'México', flag: '🇲🇽' },
  { code: '+1', country: 'Estados Unidos', flag: '🇺🇸' },
  { code: '+34', country: 'España', flag: '🇪🇸' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷' },
  { code: '+56', country: 'Chile', flag: '🇨🇱' },
  { code: '+57', country: 'Colombia', flag: '🇨🇴' },
  { code: '+51', country: 'Perú', flag: '🇵🇪' },
  { code: '+58', country: 'Venezuela', flag: '🇻🇪' },
  { code: '+593', country: 'Ecuador', flag: '🇪🇨' },
  { code: '+55', country: 'Brasil', flag: '🇧🇷' },
];

const escolaridadOptions = [
  'Primaria',
  'Secundaria',
  'Preparatoria',
  'Licenciatura',
  'Maestría',
  'Doctorado',
  'Otro'
];

const comoConocioOptions = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'google', label: 'Google' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'camion', label: 'Camión' },
  { value: 'otro', label: 'Otro' }
];

export default function AlumnoSignupPage() {
  const { tenant } = useParams<{ tenant?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Control de secciones colapsables
  const [accountSectionOpen, setAccountSectionOpen] = useState(false);
  
  // Campos del formulario - Sección 1
  const [nui, setNui] = useState('');
  const [nuiFromUrl, setNuiFromUrl] = useState(false);
  const [nombre, setNombre] = useState('');
  const [apellidoPaterno, setApellidoPaterno] = useState('');
  const [apellidoMaterno, setApellidoMaterno] = useState('');
  const [domicilio, setDomicilio] = useState('');
  const [countryCode, setCountryCode] = useState('+52');
  const [celular, setCelular] = useState('');
  const [telefonoEmergencia, setTelefonoEmergencia] = useState('');
  const [email, setEmail] = useState('');
  const [lugarNacimiento, setLugarNacimiento] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState('');
  const [nacionalidad, setNacionalidad] = useState('México');
  const [escolaridad, setEscolaridad] = useState('');
  const [ocupacion, setOcupacion] = useState('');
  const [comoConocio, setComoConocio] = useState('');
  const [comoConocioOtro, setComoConocioOtro] = useState('');
  const [tieneEnfermedad, setTieneEnfermedad] = useState(false);
  const [detalleEnfermedad, setDetalleEnfermedad] = useState('');
  const [tieneTratamiento, setTieneTratamiento] = useState(false);
  const [detalleTratamiento, setDetalleTratamiento] = useState('');
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  
  // Campos del formulario - Sección 2 (Acceso a plataforma)
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [sueno, setSueno] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);

  useEffect(() => {
    if (tenant) {
      fetchCompanyLogo();
    }
    
    // Auto-completar NUI si viene en la URL y cargar datos existentes
    const nuiParam = searchParams.get('nui');
    if (nuiParam) {
      setNui(nuiParam);
      setNuiFromUrl(true);
      fetchAlumnoData(nuiParam);
    }
    
    // Generar contraseña automáticamente al cargar la página
    const autoPassword = generateRandomPassword();
    setPassword(autoPassword);
    setConfirmPassword(autoPassword);
    setGeneratedPassword(autoPassword);
  }, [tenant, searchParams]);

  const fetchCompanyLogo = async () => {
    try {
      const response = await fetch(`/api/tenants/${tenant}/logo`);
      if (response.ok) {
        const data = await response.json();
        setCompanyLogo(data.logoUrl);
      }
    } catch (error) {
      console.log('No logo found for tenant:', tenant);
    }
  };

  // Generar contraseña aleatoria segura
  const generateRandomPassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*';
    let password = '';
    
    // Asegurar que tenga al menos 1 mayúscula, 1 minúscula, 1 número y 1 carácter especial
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]; // Mayúscula
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]; // Minúscula
    password += '0123456789'[Math.floor(Math.random() * 10)]; // Número
    password += '!@#$%&*'[Math.floor(Math.random() * 7)]; // Carácter especial
    
    // Completar el resto de la contraseña
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    
    // Mezclar los caracteres para que no sigan un patrón predecible
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  const fetchAlumnoData = async (nuiParam: string) => {
    try {
      const response = await fetch(`/api/auth/alumno/check/${tenant}/${nuiParam}`);
      
      if (!response.ok) {
        const data = await response.json();
        if (data.hasAccount) {
          setError('Este alumno ya tiene una cuenta creada. Por favor inicia sesión.');
        } else if (data.isAspirante) {
          setError('Tu solicitud aún no ha sido aceptada. Contacta a administración.');
        }
        return;
      }

      const result = await response.json();
      if (result.success && result.data) {
        const data = result.data;
        
        // Pre-llenar datos personales
        if (data.nombre) setNombre(data.nombre);
        if (data.apellidoPaterno) setApellidoPaterno(data.apellidoPaterno);
        if (data.apellidoMaterno) setApellidoMaterno(data.apellidoMaterno);
        if (data.domicilio) setDomicilio(data.domicilio);
        if (data.email) setEmail(data.email);
        
        // Pre-llenar teléfono (separar código de país del número)
        if (data.telefono) {
          const phoneMatch = data.telefono.match(/^(\+\d+)(\d{10})$/);
          if (phoneMatch) {
            setCountryCode(phoneMatch[1]);
            setCelular(phoneMatch[2]);
          } else if (data.telefono.length === 10) {
            setCelular(data.telefono);
          }
        }
        
        if (data.telefonoEmergencia) setTelefonoEmergencia(data.telefonoEmergencia);
        
        // Pre-llenar información de nacimiento
        if (data.lugarNacimiento) setLugarNacimiento(data.lugarNacimiento);
        if (data.fechaNacimiento) setFechaNacimiento(data.fechaNacimiento);
        if (data.nacionalidad) setNacionalidad(data.nacionalidad);
        
        // Pre-llenar educación y ocupación
        if (data.escolaridad) setEscolaridad(data.escolaridad);
        if (data.ocupacion) setOcupacion(data.ocupacion);
        
        // Pre-llenar marketing
        if (data.comoConocio) {
          const conocioValue = data.comoConocio.toLowerCase();
          const foundOption = comoConocioOptions.find(opt => opt.value === conocioValue);
          if (foundOption) {
            setComoConocio(foundOption.value);
          } else {
            setComoConocio('otro');
            setComoConocioOtro(data.comoConocio);
          }
        }
        
        // Pre-llenar información médica
        if (data.padecimientoMedico) {
          setTieneEnfermedad(true);
          setDetalleEnfermedad(data.padecimientoMedico);
        }
        
        if (data.tratamientoMedico) {
          setTieneTratamiento(true);
          setDetalleTratamiento(data.tratamientoMedico);
        }
        
        // Pre-llenar sueño
        if (data.sueno) setSueno(data.sueno);
      }
    } catch (error) {
      console.error('Error fetching alumno data:', error);
      // No mostrar error al usuario, solo continuar con el formulario vacío
    }
  };

  const validateForm = () => {
    // Validaciones de la Sección 1
    if (!nui) {
      setError('El NUI es obligatorio');
      return false;
    }
    if (!nombre || !apellidoPaterno || !apellidoMaterno) {
      setError('Nombre completo es obligatorio');
      return false;
    }
    if (!domicilio) {
      setError('El domicilio es obligatorio');
      return false;
    }
    if (!celular || celular.length !== 10 || !/^\d{10}$/.test(celular)) {
      setError('El celular debe tener 10 dígitos');
      return false;
    }
    if (!telefonoEmergencia || telefonoEmergencia.length !== 10 || !/^\d{10}$/.test(telefonoEmergencia)) {
      setError('El teléfono de emergencia debe tener 10 dígitos');
      return false;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('El email no es válido');
      return false;
    }
    if (!lugarNacimiento || !fechaNacimiento) {
      setError('Lugar y fecha de nacimiento son obligatorios');
      return false;
    }
    if (!nacionalidad) {
      setError('La nacionalidad es obligatoria');
      return false;
    }
    if (!escolaridad) {
      setError('La escolaridad es obligatoria');
      return false;
    }
    if (!ocupacion) {
      setError('La ocupación es obligatoria');
      return false;
    }
    if (!comoConocio) {
      setError('Por favor indica cómo nos conociste');
      return false;
    }
    if (comoConocio === 'otro' && !comoConocioOtro) {
      setError('Por favor especifica cómo nos conociste');
      return false;
    }
    if (tieneEnfermedad && !detalleEnfermedad) {
      setError('Por favor proporciona detalles de la enfermedad o alergia');
      return false;
    }
    if (tieneTratamiento && !detalleTratamiento) {
      setError('Por favor proporciona detalles del tratamiento médico');
      return false;
    }
    if (!aceptaTerminos) {
      setError('Debes aceptar los términos y condiciones para continuar');
      return false;
    }

    // Validaciones de la Sección 2 - Generar contraseña automáticamente si no hay una
    let finalPassword = password;
    
    if (!password || !confirmPassword) {
      // Generar contraseña aleatoria automáticamente
      const autoPassword = generateRandomPassword();
      setPassword(autoPassword);
      setConfirmPassword(autoPassword);
      setGeneratedPassword(autoPassword);
      finalPassword = autoPassword;
      
      // No mostrar error, solo generamos la contraseña automáticamente
      console.log('Contraseña generada automáticamente');
    } else {
      if (password !== confirmPassword) {
        setError('Las contraseñas no coinciden');
        return false;
      }
      if (password.length < 8) {
        setError('La contraseña debe tener al menos 8 caracteres');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const formData = {
        nui,
        nombre,
        apellidoPaterno,
        apellidoMaterno,
        domicilio,
        telefono: `${countryCode}${celular}`,
        telefonoEmergencia,
        email,
        lugarNacimiento,
        fechaNacimiento,
        nacionalidad,
        escolaridad,
        ocupacion,
        comoConocio: comoConocio === 'otro' ? comoConocioOtro : comoConocio,
        alergias: tieneEnfermedad,
        detalleAlergias: detalleEnfermedad,
        tratamientoMedico: tieneTratamiento,
        detalleTratamientoMedico: detalleTratamiento,
        password,
        sueno
      };

      const response = await fetch(`/api/auth/alumno/signup/${tenant}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Error al registrarse');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate(`/${tenant}/login`);
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al registrarse');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">¡Registro exitoso!</h2>
            <p className="text-gray-600">Tu cuenta ha sido creada correctamente.</p>
            
            {/* Mostrar contraseña generada automáticamente */}
            {generatedPassword && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm font-semibold text-yellow-800 mb-2">
                  ⚠️ Contraseña generada automáticamente
                </p>
                <div className="bg-white p-3 rounded border border-yellow-300 mb-2">
                  <p className="font-mono text-lg font-bold text-gray-800 select-all">
                    {generatedPassword}
                  </p>
                </div>
                <p className="text-xs text-yellow-700">
                  Por favor, guarda esta contraseña en un lugar seguro. Podrás cambiarla después de iniciar sesión.
                </p>
              </div>
            )}
            
            <p className="text-sm text-gray-500 mt-4">Revisa tu correo electrónico para más información.</p>
            <p className="text-sm text-gray-500">Redirigiendo al inicio de sesión...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4 py-8">
      <Card className="w-full max-w-3xl">
        <CardHeader className="text-center">
          <Link to="/" className="flex items-center justify-center mb-4">
            <img 
              src={companyLogo || "/qcampusone-logo.png"} 
              alt={companyLogo ? "Logo" : "QCampusOne"} 
              className="h-20 w-auto object-contain rounded-lg shadow-sm"
            />
          </Link>
          
          <div className="flex items-center justify-center gap-2 mb-2">
            <GraduationCap className="w-8 h-8 text-blue-600" />
            <CardTitle className="text-2xl">Ficha de Inscripción</CardTitle>
          </div>
          <CardDescription>
            Completa tu información para ser Alumno
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* SECCIÓN 1: INFORMACIÓN PERSONAL (Siempre visible) */}
            <div className="border rounded-lg p-6 bg-white shadow-sm space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Información Personal</h3>
              
              {/* NUI */}
              <div className="space-y-2">
                <label htmlFor="nui" className="text-sm font-medium text-gray-700">
                  NUI (Número de Identificación Único) <span className="text-red-500">*</span>
                </label>
                <Input
                  id="nui"
                  type="text"
                  placeholder="NUI-000001"
                  value={nui}
                  onChange={(e) => setNui(e.target.value)}
                  disabled={isLoading || nuiFromUrl}
                  className="uppercase"
                  required
                />
                {nuiFromUrl && (
                  <p className="text-xs text-blue-600">
                    NUI asignado automáticamente
                  </p>
                )}
              </div>

              {/* Nombre completo */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label htmlFor="nombre" className="text-sm font-medium text-gray-700">
                    Nombre(s) <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="nombre"
                    type="text"
                    placeholder="Juan"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="apellidoPaterno" className="text-sm font-medium text-gray-700">
                    Apellido Paterno <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="apellidoPaterno"
                    type="text"
                    placeholder="Pérez"
                    value={apellidoPaterno}
                    onChange={(e) => setApellidoPaterno(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="apellidoMaterno" className="text-sm font-medium text-gray-700">
                    Apellido Materno <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="apellidoMaterno"
                    type="text"
                    placeholder="López"
                    value={apellidoMaterno}
                    onChange={(e) => setApellidoMaterno(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
              </div>

              {/* Domicilio */}
              <div className="space-y-2">
                <label htmlFor="domicilio" className="text-sm font-medium text-gray-700">
                  Domicilio <span className="text-red-500">*</span>
                </label>
                <Input
                  id="domicilio"
                  type="text"
                  placeholder="Calle, Número, Colonia, Ciudad"
                  value={domicilio}
                  onChange={(e) => setDomicilio(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>

              {/* Celular con código de país */}
              <div className="space-y-2">
                <label htmlFor="celular" className="text-sm font-medium text-gray-700">
                  Celular <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    disabled={isLoading}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {countryCodes.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.code}
                      </option>
                    ))}
                  </select>
                  <Input
                    id="celular"
                    type="tel"
                    placeholder="1234567890"
                    value={celular}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 10) setCelular(value);
                    }}
                    disabled={isLoading}
                    maxLength={10}
                    required
                  />
                </div>
                <p className="text-xs text-gray-500">Ingresa 10 dígitos sin espacios</p>
              </div>

              {/* Teléfono de emergencia */}
              <div className="space-y-2">
                <label htmlFor="telefonoEmergencia" className="text-sm font-medium text-gray-700">
                  Teléfono de Emergencia <span className="text-red-500">*</span>
                </label>
                <Input
                  id="telefonoEmergencia"
                  type="tel"
                  placeholder="1234567890"
                  value={telefonoEmergencia}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 10) setTelefonoEmergencia(value);
                  }}
                  disabled={isLoading}
                  maxLength={10}
                  required
                />
                <p className="text-xs text-gray-500">10 dígitos sin espacios</p>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Correo Electrónico <span className="text-red-500">*</span>
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="correo@ejemplo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>

              {/* Lugar y Fecha de Nacimiento */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="lugarNacimiento" className="text-sm font-medium text-gray-700">
                    Lugar de Nacimiento <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="lugarNacimiento"
                    type="text"
                    placeholder="Ciudad, Estado, País"
                    value={lugarNacimiento}
                    onChange={(e) => setLugarNacimiento(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="fechaNacimiento" className="text-sm font-medium text-gray-700">
                    Fecha de Nacimiento <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="fechaNacimiento"
                    type="date"
                    value={fechaNacimiento}
                    onChange={(e) => setFechaNacimiento(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
              </div>

              {/* Nacionalidad */}
              <div className="space-y-2">
                <label htmlFor="nacionalidad" className="text-sm font-medium text-gray-700">
                  Nacionalidad <span className="text-red-500">*</span>
                </label>
                <Input
                  id="nacionalidad"
                  type="text"
                  placeholder="México"
                  value={nacionalidad}
                  onChange={(e) => setNacionalidad(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>

              {/* Escolaridad y Ocupación */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="escolaridad" className="text-sm font-medium text-gray-700">
                    Escolaridad <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="escolaridad"
                    value={escolaridad}
                    onChange={(e) => setEscolaridad(e.target.value)}
                    disabled={isLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Selecciona...</option>
                    {escolaridadOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label htmlFor="ocupacion" className="text-sm font-medium text-gray-700">
                    Ocupación <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="ocupacion"
                    type="text"
                    placeholder="Estudiante, Empleado, etc."
                    value={ocupacion}
                    onChange={(e) => setOcupacion(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
              </div>

              {/* Cómo nos conociste */}
              <div className="space-y-2">
                <label htmlFor="comoConocio" className="text-sm font-medium text-gray-700">
                  ¿Cómo nos conociste? <span className="text-red-500">*</span>
                </label>
                <select
                  id="comoConocio"
                  value={comoConocio}
                  onChange={(e) => setComoConocio(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Selecciona...</option>
                  {comoConocioOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {comoConocio === 'otro' && (
                  <Input
                    type="text"
                    placeholder="Especifica cómo nos conociste"
                    value={comoConocioOtro}
                    onChange={(e) => setComoConocioOtro(e.target.value)}
                    disabled={isLoading}
                    className="mt-2"
                    required
                  />
                )}
              </div>

              {/* Enfermedad o Alergia */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  ¿Padeces alguna enfermedad o alergia? <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="tieneEnfermedad"
                      checked={tieneEnfermedad === false}
                      onChange={() => setTieneEnfermedad(false)}
                      disabled={isLoading}
                      className="w-4 h-4"
                    />
                    <span>No</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="tieneEnfermedad"
                      checked={tieneEnfermedad === true}
                      onChange={() => setTieneEnfermedad(true)}
                      disabled={isLoading}
                      className="w-4 h-4"
                    />
                    <span>Sí</span>
                  </label>
                </div>
                {tieneEnfermedad && (
                  <Textarea
                    placeholder="Describe la enfermedad o alergia"
                    value={detalleEnfermedad}
                    onChange={(e) => setDetalleEnfermedad(e.target.value)}
                    disabled={isLoading}
                    className="mt-2"
                    rows={3}
                    required
                  />
                )}
              </div>

              {/* Tratamiento Médico */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  ¿Estás bajo algún tratamiento médico? <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="tieneTratamiento"
                      checked={tieneTratamiento === false}
                      onChange={() => setTieneTratamiento(false)}
                      disabled={isLoading}
                      className="w-4 h-4"
                    />
                    <span>No</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="tieneTratamiento"
                      checked={tieneTratamiento === true}
                      onChange={() => setTieneTratamiento(true)}
                      disabled={isLoading}
                      className="w-4 h-4"
                    />
                    <span>Sí</span>
                  </label>
                </div>
                {tieneTratamiento && (
                  <Textarea
                    placeholder="Describe el tratamiento médico"
                    value={detalleTratamiento}
                    onChange={(e) => setDetalleTratamiento(e.target.value)}
                    disabled={isLoading}
                    className="mt-2"
                    rows={3}
                    required
                  />
                )}
              </div>

              {/* Términos y Condiciones */}
              <div className="space-y-2">
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={aceptaTerminos}
                    onChange={(e) => setAceptaTerminos(e.target.checked)}
                    disabled={isLoading}
                    className="w-4 h-4 mt-1"
                    required
                  />
                  <span className="text-sm text-gray-700">
                    Acepto los <a href="#" className="text-blue-600 hover:underline">términos y condiciones</a> y el <a href="#" className="text-blue-600 hover:underline">aviso de privacidad</a> <span className="text-red-500">*</span>
                  </span>
                </label>
              </div>
            </div>

            {/* SECCIÓN 2: ACCESO A LA PLATAFORMA (Colapsable) */}
            <div className="border rounded-lg bg-white shadow-sm">
              <button
                type="button"
                onClick={() => setAccountSectionOpen(!accountSectionOpen)}
                className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
              >
                <h3 className="text-lg font-semibold text-gray-900">Registro para Acceso a la Plataforma</h3>
                {accountSectionOpen ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              
              {accountSectionOpen && (
                <div className="px-6 pb-6 space-y-4 border-t">
                  <div className="pt-4 space-y-4">
                    {/* Contraseña */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label htmlFor="password" className="text-sm font-medium text-gray-700">
                          Contraseña <span className="text-gray-400">(opcional)</span>
                        </label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newPassword = generateRandomPassword();
                            setPassword(newPassword);
                            setConfirmPassword(newPassword);
                            setGeneratedPassword(newPassword);
                          }}
                          className="text-xs"
                          disabled={isLoading}
                        >
                          🎲 Generar automática
                        </Button>
                      </div>
                      <Input
                        id="password"
                        type="password"
                        placeholder="Mínimo 8 caracteres (se generará automáticamente si la dejas vacía)"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={isLoading}
                      />
                      <p className="text-xs text-gray-500">
                        💡 Si no capturas una contraseña, el sistema generará una segura automáticamente
                      </p>
                    </div>

                    {/* Confirmar Contraseña */}
                    <div className="space-y-2">
                      <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                        Confirmar Contraseña <span className="text-gray-400">(opcional)</span>
                      </label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="Repite tu contraseña"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>

                    {/* Sueño */}
                    <div className="space-y-2">
                      <label htmlFor="sueno" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-yellow-500" />
                        ¿Cuál es tu sueño?
                      </label>
                      <Textarea
                        id="sueno"
                        placeholder="Comparte tu sueño, tu meta, lo que te motiva a estudiar..."
                        value={sueno}
                        onChange={(e) => setSueno(e.target.value)}
                        disabled={isLoading}
                        rows={3}
                        className="resize-none"
                      />
                      <p className="text-xs text-gray-500 italic">
                        Este mensaje te inspirará durante tu camino académico
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Error Display */}
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Botón de envío */}
            <Button 
              type="submit" 
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Creando Cuenta...' : 'Crear Cuenta'}
            </Button>

            {/* Link a login */}
            <div className="text-center text-sm text-gray-600">
              ¿Ya tienes cuenta?{' '}
              <Link 
                to={`/${tenant}/login`} 
                className="text-blue-600 hover:underline font-medium"
              >
                Inicia sesión aquí
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
